import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import axios from "axios";

const defaults = {
  stripePaymentOwner: "platform",
  stripeMode: "live",
  mode: "live",
  testSecretKey: "",
  testPublishableKey: "",
  testWebhookSecret: "",
  stripeWebhookSecretTest: "",
  liveSecretKey: "",
  livePublishableKey: "",
  liveWebhookSecret: "",
  stripeWebhookSecretLive: "",
  stripeConnectAccountId: "",
  connectStatus: "",
};

export default function StripeSettings({ instanceUniqueId }) {
  const SERVER = process.env.REACT_APP_NODEJS_SERVER_URL;
  const headers = { headers: { accessToken: localStorage.getItem("accessToken") } };

  const [form, setForm] = useState(defaults);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [lastMode, setLastMode] = useState(null);
  const [permissions, setPermissions] = useState({
    canEditOwner: false,
    canManageStripe: false,
    canEditKeys: false,
  });
  const [connectLoading, setConnectLoading] = useState(false);

  const setField = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const load = async () => {
    if (!instanceUniqueId) return;
    setLoading(true);
    try {
      const res = await axios.get(`${SERVER}/instances/${instanceUniqueId}/stripe-settings`, headers);
      const settings = res.data?.settings || {};
      setForm({ ...defaults, ...settings, mode: settings.mode || settings.stripeMode || "live" });
      setPermissions(res.data?.permissions || {
        canEditOwner: false,
        canManageStripe: false,
        canEditKeys: false,
      });
      setLastMode(settings.mode || settings.stripeMode || null);
    } catch (e) {
      alert(e?.response?.data?.error || "Failed to load Stripe settings.");
    } finally {
      setLoading(false);
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        ...form,
        stripeMode: form.mode === "test" ? "test" : "live",
        mode: form.mode === "test" ? "test" : "live",
      };
      const res = await axios.put(
        `${SERVER}/instances/${instanceUniqueId}/stripe-settings`,
        payload,
        headers,
      );
      const settings = res.data?.settings || payload;
      setForm({ ...defaults, ...settings, mode: settings.mode || settings.stripeMode || payload.mode });
      setPermissions(res.data?.permissions || permissions);
      setLastMode(settings.mode || settings.stripeMode || payload.mode);
      alert("Stripe settings saved.");
    } catch (e) {
      alert(e?.response?.data?.error || "Failed to save Stripe settings.");
    } finally {
      setSaving(false);
    }
  };

  const canManageStripe = Boolean(permissions.canManageStripe || permissions.canEditKeys);
  const canEditInstanceKeys = canManageStripe && form.stripePaymentOwner === "instance";
  const canEditMode = canEditInstanceKeys;

  const instanceKeysHelperText = canEditInstanceKeys
    ? "Enter both test and live keys, then choose the active mode used for Stripe calls."
    : "You do not have permission to edit instance Stripe credentials.";

  const startConnectOnboarding = async () => {
    setConnectLoading(true);
    try {
      const res = await axios.post(
        `${SERVER}/api/stripe/connect/onboard`,
        { instanceUniqueId },
        headers,
      );
      const settings = res.data?.settings || form;
      setForm({ ...form, ...settings, mode: settings.mode || settings.stripeMode || form.mode });
      if (res.data?.onboardingUrl) {
        window.open(res.data.onboardingUrl, "_blank");
      }
    } catch (e) {
      alert(e?.response?.data?.error || "Failed to start onboarding.");
    } finally {
      setConnectLoading(false);
    }
  };

  const refreshConnectStatus = async () => {
    setConnectLoading(true);
    try {
      const res = await axios.get(
        `${SERVER}/api/stripe/connect/status`,
        { ...headers, params: { instanceUniqueId } },
      );
      const settings = { ...form, connectStatus: res.data?.connectStatus, stripeConnectAccountId: res.data?.stripeConnectAccountId };
      setForm(settings);
    } catch (e) {
      alert(e?.response?.data?.error || "Failed to refresh status.");
    } finally {
      setConnectLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [instanceUniqueId]);

  return (
    <Card elevation={3} sx={{ borderRadius: 2 }}>
      <CardHeader
        title={
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Typography variant="h6">Stripe</Typography>
            {loading ? (
              <CircularProgress size={16} />
            ) : (
              <Chip
                size="small"
                color={form.mode === "test" ? "warning" : "success"}
                label={`Mode: ${form.mode === "test" ? "Test" : "Live"}`}
              />
            )}
            {lastMode && lastMode !== form.mode && (
              <Chip size="small" color="info" label={`Previously ${lastMode}`} />
            )}
          </Box>
        }
        action={
          <Box sx={{ display: "flex", gap: 1 }}>
            <Button variant="outlined" onClick={load} disabled={loading || saving}>
              Refresh
            </Button>
            <Button variant="contained" onClick={save} disabled={saving || loading}>
              {saving ? "Saving..." : "Save"}
            </Button>
          </Box>
        }
        subheader="Manage Stripe credentials per instance and choose the mode used for all Stripe calls."
      />
      <Divider />
      <CardContent>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={4}>
            <FormControl fullWidth>
              <InputLabel id="stripe-owner-label">Payment owner</InputLabel>
              <Select
                labelId="stripe-owner-label"
                label="Payment owner"
                value={form.stripePaymentOwner}
                onChange={setField("stripePaymentOwner")}
                disabled={!permissions.canEditOwner || saving}
              >
                <MenuItem value="platform">Elite Core Platform</MenuItem>
                <MenuItem value="instance">Instance Keys</MenuItem>
                <MenuItem value="connect">Stripe Connect</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <FormControl fullWidth>
              <InputLabel id="stripe-mode-label">Active mode</InputLabel>
              <Select
                labelId="stripe-mode-label"
                label="Active mode"
                value={form.mode}
                onChange={setField("mode")}
                disabled={!canEditMode || saving}
              >
                <MenuItem value="live">Live</MenuItem>
                <MenuItem value="test">Test</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>

        {form.stripePaymentOwner === "instance" && (
          <>
            <Typography variant="body2" sx={{ mt: 3 }} color="text.secondary">
              {instanceKeysHelperText}
            </Typography>
            <Typography variant="subtitle1" sx={{ mt: 3, mb: 1, fontWeight: 600 }}>
              Test keys
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  type="password"
                  label="Test Secret Key"
                  value={form.testSecretKey}
                  onChange={setField("testSecretKey")}
                  placeholder="sk_test_..."
                  disabled={!canEditInstanceKeys || saving}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  label="Test Publishable Key"
                  value={form.testPublishableKey}
                  onChange={setField("testPublishableKey")}
                  placeholder="pk_test_..."
                  disabled={!canEditInstanceKeys || saving}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  type="password"
                  label="Test Webhook Secret"
                  value={form.testWebhookSecret || form.stripeWebhookSecretTest}
                  onChange={setField("testWebhookSecret")}
                  placeholder="whsec_..."
                  disabled={!canEditInstanceKeys || saving}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  type="password"
                  label="Test Webhook Secret (override)"
                  value={form.stripeWebhookSecretTest}
                  onChange={setField("stripeWebhookSecretTest")}
                  placeholder="whsec_..."
                  disabled={!canEditInstanceKeys || saving}
                />
              </Grid>
            </Grid>

            <Typography variant="subtitle1" sx={{ mt: 3, mb: 1, fontWeight: 600 }}>
              Live keys
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  type="password"
                  label="Live Secret Key"
                  value={form.liveSecretKey}
                  onChange={setField("liveSecretKey")}
                  placeholder="sk_live_..."
                  disabled={!canEditInstanceKeys || saving}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  label="Live Publishable Key"
                  value={form.livePublishableKey}
                  onChange={setField("livePublishableKey")}
                  placeholder="pk_live_..."
                  disabled={!canEditInstanceKeys || saving}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  type="password"
                  label="Live Webhook Secret"
                  value={form.liveWebhookSecret || form.stripeWebhookSecretLive}
                  onChange={setField("liveWebhookSecret")}
                  placeholder="whsec_..."
                  disabled={!canEditInstanceKeys || saving}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  type="password"
                  label="Live Webhook Secret (override)"
                  value={form.stripeWebhookSecretLive}
                  onChange={setField("stripeWebhookSecretLive")}
                  placeholder="whsec_..."
                  disabled={!canEditInstanceKeys || saving}
                />
              </Grid>
            </Grid>
          </>
        )}

        {form.stripePaymentOwner === "platform" && (
          <Typography variant="body2" sx={{ mt: 3 }} color="text.secondary">
            Using Elite Core platform keys. Switch Payment owner to Instance Keys to enter per-instance Stripe credentials.
          </Typography>
        )}

        {form.stripePaymentOwner === "connect" && (
          <Box sx={{ mt: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Stripe Connect</Typography>
              {form.connectStatus && (
                <Chip
                  size="small"
                  color={form.connectStatus === 'onboarded' ? 'success' : 'warning'}
                  label={`Status: ${form.connectStatus}`}
                />
              )}
              {connectLoading && <CircularProgress size={14} />}
            </Box>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Connect Account ID"
                  value={form.stripeConnectAccountId || ''}
                  disabled
                />
              </Grid>
            </Grid>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                variant="contained"
                onClick={startConnectOnboarding}
                disabled={connectLoading || saving || !permissions.canEditOwner}
              >
                Start onboarding
              </Button>
              <Button
                variant="outlined"
                onClick={refreshConnectStatus}
                disabled={connectLoading || saving}
              >
                Refresh status
              </Button>
            </Box>
          </Box>
        )}
      </CardContent>
    </Card>
  );
}
